/**
 * odds.ts — Pure odds calculation for the Kudos betting system.
 *
 * No DB calls. All inputs are plain data so this can be called from
 * server actions, API routes, and client-side previews alike.
 *
 * Multiplier semantics:
 *   odds = 1 / probability
 *   A 50/50 matchup → both sides 2.0×
 *   Heavy favourite → ~1.1×   Massive underdog → up to 10×
 *
 * No house edge — this is family fun, not a sportsbook.
 */

export interface NameStats {
  winCount: number;
  lossCount: number;
  winStreak: number;
  resurrected: boolean; // LCK survivor
  eliminated: boolean;
}

// ─── Match odds ───────────────────────────────────────────────────────────────
// Returns a multiplier for EACH name in the matchup.
// The two multipliers are independent — both are fair prices, not a zero-sum book.

export function calcMatchOdds(
  nameA: NameStats,
  nameB: NameStats
): { oddsA: number; oddsB: number } {
  const probA = rawProbability(nameA);
  const probB = rawProbability(nameB);

  // Normalise so the two probs sum to 1.0 (pure head-to-head)
  const total = probA + probB || 1;
  const normA = probA / total;
  const normB = probB / total;

  return {
    oddsA: clampMatchOdds(1 / normA),
    oddsB: clampMatchOdds(1 / normB),
  };
}

// ─── Futures odds ─────────────────────────────────────────────────────────────
// Odds for a name to win the entire tournament.
// More volatile than match odds — underdogs can hit 25×.

export function calcFuturesOdds(name: NameStats, totalActiveNames: number): number {
  const base = rawProbability(name);

  // Scale by how many names are still alive — deeper field = longer odds overall
  const fieldFactor = Math.max(totalActiveNames, 2) / 2;
  const adjusted = base / fieldFactor;

  return clampFuturesOdds(1 / Math.max(adjusted, 0.01));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Raw probability score for a name — NOT normalised to 0..1 on its own,
 * only meaningful when compared against an opponent or field.
 */
function rawProbability(name: NameStats): number {
  const games = name.winCount + name.lossCount;
  const winRate = games > 0 ? name.winCount / games : 0.5;

  // Streak bonus: each consecutive win adds +4%, capped at +20%
  const streakBonus = Math.min(name.winStreak * 0.04, 0.20);

  // LCK penalty: coming back from the dead is heroic but still a disadvantage
  const lckPenalty = name.resurrected ? 0.15 : 0;

  return Math.max(winRate + streakBonus - lckPenalty, 0.05);
}

function clampMatchOdds(raw: number): number {
  // 1.05× (near-certainty) → 10× (huge underdog)
  return parseFloat(Math.min(Math.max(raw, 1.05), 10.0).toFixed(2));
}

function clampFuturesOdds(raw: number): number {
  // 1.10× (dominant name) → 25× (long shot)
  return parseFloat(Math.min(Math.max(raw, 1.1), 25.0).toFixed(2));
}

// ─── Display helpers ─────────────────────────────────────────────────────────

/** Human-readable label: "2.4×", "EVEN", "Heavy fav" */
export function oddsLabel(odds: number): string {
  if (odds <= 1.15) return "Heavy fav";
  if (odds <= 1.5) return "Favourite";
  if (odds >= 1.95 && odds <= 2.05) return "Even";
  if (odds >= 4.0) return "Underdog";
  return `${odds.toFixed(1)}×`;
}

/** Badge colour class based on odds bracket */
export function oddsBadgeClass(odds: number): string {
  if (odds <= 1.5) return "text-green-700 bg-green-100";
  if (odds <= 2.5) return "text-blue-700 bg-blue-100";
  if (odds <= 5.0) return "text-amber-700 bg-amber-100";
  return "text-red-700 bg-red-100";
}
